import { UnitEconomicsTemplate, TemplateExecution } from '../types/templateDesigner';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  count?: number;
  timestamp: string;
}

export class TemplateService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }

  /**
   * Create a new template
   */
  async createTemplate(templateData: Omit<UnitEconomicsTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<UnitEconomicsTemplate> {
    const response = await fetch(`${API_BASE_URL}/api/templates`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(templateData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create template');
    }

    const result: ApiResponse<UnitEconomicsTemplate> = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }

  /**
   * Get all templates for the current user
   */
  async getTemplates(filters?: { category?: string }): Promise<UnitEconomicsTemplate[]> {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);

    const url = `${API_BASE_URL}/api/templates${params.toString() ? '?' + params.toString() : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch templates');
    }

    const result: ApiResponse<UnitEconomicsTemplate[]> = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }

  /**
   * Get a single template by ID
   */
  async getTemplate(templateId: string): Promise<UnitEconomicsTemplate> {
    const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch template');
    }

    const result: ApiResponse<UnitEconomicsTemplate> = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }

  /**
   * Update a template
   */
  async updateTemplate(templateId: string, updateData: Partial<UnitEconomicsTemplate>): Promise<UnitEconomicsTemplate> {
    const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update template');
    }

    const result: ApiResponse<UnitEconomicsTemplate> = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }

  /**
   * Delete a template
   */
  async deleteTemplate(templateId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete template');
    }
  }

  /**
   * Clone a template
   */
  async cloneTemplate(templateId: string, newName?: string): Promise<UnitEconomicsTemplate> {
    const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}/clone`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ name: newName })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to clone template');
    }

    const result: ApiResponse<UnitEconomicsTemplate> = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }

  /**
   * Execute a template
   */
  async executeTemplate(
    templateId: string,
    inputs: any,
    context: { month: string; cloudCosts: number; customCosts: number }
  ): Promise<TemplateExecution> {
    const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}/execute`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        inputs,
        month: context.month,
        cloudCosts: context.cloudCosts,
        customCosts: context.customCosts
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to execute template');
    }

    const result: ApiResponse<TemplateExecution> = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }

  /**
   * Execute a draft (unsaved) template by sending the template body inline.
   */
  async executeDraftTemplate(
    template: any,
    inputs: any,
    context: { month: string; cloudCosts?: number; customCosts?: number }
  ): Promise<TemplateExecution> {
    const response = await fetch(`${API_BASE_URL}/api/templates/execute-draft`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        template,
        inputs,
        month: context.month,
        cloudCosts: context.cloudCosts ?? 0,
        customCosts: context.customCosts ?? 0
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to execute draft template');
    }

    const result: ApiResponse<TemplateExecution> = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }
    return result.data;
  }

  /**
   * Get execution history for a template
   */
  async getExecutionHistory(templateId: string, limit: number = 10): Promise<TemplateExecution[]> {
    const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}/history?limit=${limit}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch execution history');
    }

    const result: ApiResponse<TemplateExecution[]> = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }

  /**
   * Get public templates from marketplace
   */
  async getPublicTemplates(category?: string): Promise<UnitEconomicsTemplate[]> {
    const params = category ? `?category=${category}` : '';
    const response = await fetch(`${API_BASE_URL}/api/templates/marketplace/public${params}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch public templates');
    }

    const result: ApiResponse<UnitEconomicsTemplate[]> = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }

  /**
   * Import a public template to user's collection
   */
  async importPublicTemplate(publicTemplateId: string): Promise<UnitEconomicsTemplate> {
    const response = await fetch(`${API_BASE_URL}/api/templates/marketplace/import/${publicTemplateId}`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to import template');
    }

    const result: ApiResponse<UnitEconomicsTemplate> = await response.json();
    if (!result.success || !result.data) {
      throw new Error('Invalid response from server');
    }

    return result.data;
  }
}

export default new TemplateService();
